import { useEffect, useRef } from 'react';

import qlog from 'views_shared/app/javascript/scripts/qlog';
import { accessToken } from 'views_shared/app/javascript/scripts/authentication';
import { refreshAccessToken } from 'views_shared/app/javascript/api/withTokenRefresh';

// Non-SUBSCRIBED statuses get reported — catches tier-limit hits + transient drops.
// CLOSED is excluded: it fires on intentional teardown (the tearDown listener below + useEffect
// cleanup), so reporting it would spam Rollbar on every navigation away from /my_inbox_v2.
// See https://supabase.com/docs/guides/realtime/limits
const UNHEALTHY_STATUSES = new Set(['CHANNEL_ERROR', 'TIMED_OUT']);

// Transient drops auto-recover via realtime-js's rejoin backoff and re-emit SUBSCRIBED, so they're
// noise. Only report a channel still unhealthy this long after it first went unhealthy.
const RECOVERY_GRACE_MS = 30_000;

/**
 * Inbox-wide Supabase Realtime subscription on interact_receipts.
 *
 * Listens for new receipts for the current user across ALL conversations,
 * then routes events to the appropriate handler:
 * - onActiveConversationMessage: called when the event matches the currently viewed conversation
 * - onAnyConversationMessage: called for every incoming event (e.g., to refresh the sidebar)
 *
 * @param {string} currentReceiverProfileId - The current user's profile slug
 * @param {string|null} activeConversationId - The currently viewed conversation ID
 * @param {object} handlers
 * @param {function} handlers.onActiveConversationMessage - Called when a message arrives for the active conversation
 * @param {function} handlers.onAnyConversationMessage - Called when a message arrives for any conversation
 */
function useInboxRealtime(
  currentReceiverProfileId,
  activeConversationId,
  { onActiveConversationMessage, onAnyConversationMessage },
) {
  // Refs for volatile values so the subscription only re-creates when the user's slug changes
  const activeConversationIdRef = useRef(activeConversationId);
  activeConversationIdRef.current = activeConversationId;

  const onActiveRef = useRef(onActiveConversationMessage);
  onActiveRef.current = onActiveConversationMessage;

  const onAnyRef = useRef(onAnyConversationMessage);
  onAnyRef.current = onAnyConversationMessage;

  useEffect(() => {
    if (!currentReceiverProfileId) return () => {};

    const { supabaseClient } = globalThis.Qwoted;
    const channelName = `inbox-${currentReceiverProfileId}`;

    let tearingDown = false;
    let hadError = false;
    let lastResumedAt = 0;
    // Recovery-grace state (see RECOVERY_GRACE_MS); cleared on SUBSCRIBED, rebuild, and teardown.
    let recoveryTimer = null;
    let reportedUnhealthyEpisode = false;
    let channelGeneration = 0; // bumped on each (re)build so stale channel callbacks can bail
    const clearRecoveryTimer = () => {
      if (recoveryTimer) {
        clearTimeout(recoveryTimer);
        recoveryTimer = null;
      }
    };

    const buildChannel = () => {
      const ch = supabaseClient.channel(channelName);
      channelGeneration += 1;
      const thisGeneration = channelGeneration; // identifies THIS channel's callbacks

      ch.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: globalThis.Qwoted.POSTGRES_SCHEMA,
          table: 'interact_receipts',
          filter: `receiver_profile_id=eq.${currentReceiverProfileId}`,
        },
        (payload) => {
          // Admins may receive events for other users' receipts due to broad RLS policies; ignore those.
          if (payload.new.receiver_profile_id !== currentReceiverProfileId) return;

          setTimeout(() => {
            // Refresh the conversations list — pass the affected conversation id so the
            // handler can sync just that row instead of refetching the whole page.
            onAnyRef.current?.(payload.new.denormalized_conversation_id);

            // Also refresh messages if this event is for the conversation the user is viewing
            if (payload.new.denormalized_conversation_id === activeConversationIdRef.current) {
              onActiveRef.current?.();
            }
          }, 250); // Materialized views may not be refreshed yet when the websocket event arrives
        },
      );

      ch.subscribe((status, err) => {
        // Ignore callbacks from a channel that's since been replaced (e.g. by a refocus rebuild).
        if (thisGeneration !== channelGeneration) return;
        if (status === 'SUBSCRIBED') {
          qlog.debug(`Realtime subscribed: ${channelName}`);
          clearRecoveryTimer();
          reportedUnhealthyEpisode = false;
          // Realtime doesn't replay events missed while disconnected; refetch to backfill the gap.
          if (hadError) {
            hadError = false;
            onAnyRef.current?.();
            if (activeConversationIdRef.current) onActiveRef.current?.();
          }
          return;
        }
        if (UNHEALTHY_STATUSES.has(status)) {
          hadError = true;
          const message = `Realtime channel ${channelName} entered ${status}`;
          qlog.warn(message, err);
          // Don't report drops we can't act on: teardown, or while backgrounded/just-refocused.
          const benign = tearingDown
            || document.visibilityState === 'hidden'
            || Date.now() - lastResumedAt < 10_000;
          // Arm a grace timer instead of reporting now; SUBSCRIBED clears it if the channel recovers.
          // Once per episode: don't re-arm while pending or after a report.
          if (!benign && !recoveryTimer && !reportedUnhealthyEpisode) {
            recoveryTimer = setTimeout(() => {
              recoveryTimer = null;
              reportedUnhealthyEpisode = true;
              window.Rollbar?.error(
                `${message} and did not recover within ${RECOVERY_GRACE_MS}ms`,
                err,
              );
            }, RECOVERY_GRACE_MS);
          }
        }
      });

      return ch;
    };

    let channel = buildChannel();

    const onVisibility = async () => {
      if (tearingDown) return; // torn down: don't resurrect the channel off-inbox
      if (document.visibilityState !== 'visible') return;
      lastResumedAt = Date.now();
      // Realtime caches the JWT from page load and can't rejoin once it expires; refresh the
      // cookie if it's gone, then hand realtime a fresh token so the channel self-heals.
      if (!accessToken()) await refreshAccessToken();
      if (tearingDown) return; // teardown may have run during the await
      const token = accessToken();
      if (token) supabaseClient.realtime.setAuth(token);
      // setAuth won't rejoin a channel that died while hidden — reopen it if it's no longer joined.
      if (channel && channel.state !== 'joined') {
        clearRecoveryTimer();
        reportedUnhealthyEpisode = false;
        supabaseClient.removeChannel(channel);
        channel = buildChannel();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    // react_ujs 2.6.1's Turbolinks 5 integration doesn't unmount React on navigation, so this
    // useEffect's cleanup never runs when the user leaves /my_inbox_v2 via Turbolinks. Without
    // this, the channel stays subscribed in JS memory after navigation, reporting CHANNEL_ERROR
    // from off-inbox URLs on every WebSocket disconnect. See doc/inbox-channel-error-investigation.md.
    // Idempotency guard: protects against the listener and useEffect cleanup both firing, or
    // against Turbolinks emitting related events more than once during a single navigation.
    let tornDown = false;
    const tearDown = () => {
      if (tornDown) return;
      tornDown = true;
      tearingDown = true;
      channelGeneration += 1; // invalidate the live channel's in-flight callbacks
      clearRecoveryTimer();
      document.removeEventListener('visibilitychange', onVisibility); // no refocus rebuilds after teardown
      document.removeEventListener('turbolinks:before-cache', tearDown);
      supabaseClient.removeChannel(channel);
    };
    document.addEventListener('turbolinks:before-cache', tearDown);

    return () => {
      tearingDown = true;
      clearRecoveryTimer();
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('turbolinks:before-cache', tearDown);
      supabaseClient.removeChannel(channel);
    };
  }, [currentReceiverProfileId]);
}

export default useInboxRealtime;
